import type { ReactNode } from 'react';
import React, { memo, useState } from 'react';
import { Descriptions } from 'antd';
import type { DescriptionsItemProps } from 'antd/lib/descriptions/Item';
import type { DescriptionsProps } from 'antd/lib/descriptions';
import type { CReactNode, ObjectMap, OperateOption } from '@bluevision/tools';
import {
  useMount,
  formatTime,
  numberFormat,
  str2Arr,
  isArray,
  isBoolean,
  isObj,
  isStr,
  placeholder,
} from '@bluevision/tools';

import CopyableText from '@/components/CopyableText';
import MultipleRender from '@/components/Render/MultipleRender';
import { booleanMap } from '@/data';
import i18n from '@/i18n';

import TextOverflow from '../TextOverflow';

import styles from './style.less';
import classNames from 'classnames';

// handle history code
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DescriptionsItem<T = any> = {
  // handle history code
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  render?: (x: any, info: T) => ReactNode | string;
  labelWidth?: number;
  hidden?: boolean;
  title?: string | ReactNode;
  key: string | string[];
} & Omit<DescriptionsItemProps, 'children'> &
  Omit<OperateOption, 'title' | 'key'>;
type Props = {
  options: DescriptionsItem[];
  // handle history code
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  labelWidth?: number;
  column?: number;
  textAlign?: 'right' | 'left' | 'center';
} & Omit<DescriptionsProps, 'column'>;

// handle history code
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getIn = (p: string[], o: ObjectMap<any>) => {
  return p.reduce((xs, x) => xs?.[x], o);
};

const renderTitle: (
  options: DescriptionsItem & { textAlign: Props['textAlign'] },
) => CReactNode = ({ title, labelWidth, textAlign }) => {
  if (title) {
    return (
      <TextOverflow
        style={{ width: labelWidth, textAlign }}
        value={typeof title === 'string' ? i18n.t(title) : title}
      />
    );
  }
  return placeholder;
};

// handle history code
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderItem = (option: DescriptionsItem, data: any) => {
  const { key, render, copyable, isEnum, isNum, isTime, isMultiple, isBool } = option;

  const value = isStr(key) ? data?.[key as string] : getIn(key as string[], data);
  if (copyable === false) {
    return value;
  }
  if (isEnum) {
    return i18n.t(isEnum[value]) ?? value ?? placeholder;
  }
  if (isNum) {
    return numberFormat(value, (isBoolean(isNum) ? undefined : isNum) as number);
  }
  if (isTime) {
    return formatTime(value, (isBoolean(isTime) ? undefined : isTime) as string);
  }
  if (isBool) {
    return i18n.t(booleanMap[value]);
  }
  if (isMultiple) {
    if (isBoolean(isMultiple) || isStr(isMultiple)) {
      return (
        <MultipleRender
          value={str2Arr(value)}
          text={(isBoolean(isMultiple) ? undefined : isMultiple) as string}
        />
      );
    }
    if (isObj(isMultiple) || isArray(isMultiple)) {
      return (
        <MultipleRender
          // handle history code
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          value={str2Arr(value).map((item: any) => isMultiple[item] || item)}
          text={i18n.t('individual')}
        />
      );
    }
  }
  if (render) {
    return render(value, data);
  }
  return <CopyableText value={value} />;
};

const CDescriptions: React.FC<Props> = (props) => {
  const {
    data,
    options,
    labelWidth = 80,
    column = 2,
    textAlign = 'right',
    className,
    ...r
  } = props;

  // handle history code
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [width, setWidth] = useState<any>();

  useMount(() => {
    const contentWidth = document.getElementById('c-description')?.offsetWidth;
    if (contentWidth) {
      let singleWidth = contentWidth / column - labelWidth - 20;
      if (singleWidth < 0) {
        singleWidth = 0;
      }
      setWidth(singleWidth);
    }
  });

  return (
    <div id='c-description'>
      <Descriptions bordered column={column} {...r} className={classNames(styles.cDesc, className)}>
        {options
          .filter(({ hidden }) => !hidden)
          .map((option) => {
            const {
              key,
              contentStyle,
              span = 1,
              render,
              hidden,
              title,
              copyable,
              ...item
            } = option;
            return (
              <Descriptions.Item
                key={key as string}
                labelStyle={{ width: `${labelWidth}px` }}
                contentStyle={{
                  width: `${width * span + (span - 1) * labelWidth}px`,
                  ...contentStyle,
                }}
                span={span}
                label={
                  <div style={{ width: labelWidth, textAlign }}>
                    {renderTitle({ ...option, textAlign })}
                  </div>
                }
                {...item}
              >
                {renderItem(option, data)}
              </Descriptions.Item>
            );
          })}
      </Descriptions>
    </div>
  );
};
export default memo(CDescriptions);
